/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import * as React from "react";
import { I18n } from "i18n-js";
import * as locales from "../../../locales.json";
const i18n = new I18n(locales);
i18n.defaultLocale = "en";
i18n.locale = (window as any).locale || "";
import ScaleLoader from "react-spinners/ScaleLoader";
import { ErrorBoundary } from "react-error-boundary";
import { datadogRum } from "@datadog/browser-rum";

import * as API from "../../../api/billing";
import ErrorFallbackCard from "../../reusable/errorFallbackCard";
import RecentInvoicesCard from "./components/recentInvoicesCard";
import RewardBalanceCard from "./components/rewardBalanceCard";
import ToastMessage from "../../reusable/toastMessage";
import setUpAxios from "../../../helperFunctions/helperFunctions";
import { ContainerData, ToastMessageOptionTypes } from "./lib/types";
import {
  StyledContainer,
  StyledMasonry,
  StyledPageLoaderContainer,
  StyledPageHeader,
} from "./lib/styles";
import { defaultMessageOptions } from "./lib/constants";
import { initializeDatadogRUM } from "../../../utils";
import { theme } from "../../theme/theme";

const BillingActivityContainer = (): React.ReactElement => {
  const [data, setData] = React.useState<ContainerData>({});
  const [loading, setLoading] = React.useState<boolean>(true);
  const [
    toastMessageOptions,
    setToastMessageOptions,
  ] = React.useState<ToastMessageOptionTypes>(defaultMessageOptions);

  const updateToastMessage = (options: ToastMessageOptionTypes): void => {
    setToastMessageOptions(options);
  };

  React.useEffect(() => {
    initializeDatadogRUM();
    // Call once in this container then all cards within this container
    // will have axios setup without needing to call it again
    setUpAxios();

    const fetchData = async (): Promise<void> => {
      try {
        const response = await API.getCompanyBillingPage();
        setData(response.data.data);
      } catch (error) {
        datadogRum.addError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <StyledContainer>
      <StyledPageHeader>
        {i18n.translate("title.billing_activity")}
      </StyledPageHeader>
      {loading ? (
        <StyledPageLoaderContainer data-testid="loader">
          <ScaleLoader
            color={theme.color.grayDark}
            loading={loading}
            height={45}
            width={6}
            margin={6}
          />
        </StyledPageLoaderContainer>
      ) : (
        <StyledMasonry
          breakpointCols="1"
          className="billing-activity-grid"
          columnClassName="grid-column"
          data-testid="billing-activity"
        >
          <ErrorBoundary FallbackComponent={ErrorFallbackCard}>
            <RecentInvoicesCard
              showSuperAdminFeature={data.showSuperAdminFeature}
              updateToastMessage={updateToastMessage}
            />
          </ErrorBoundary>
          <ErrorBoundary FallbackComponent={ErrorFallbackCard}>
            <RewardBalanceCard
              showSuperAdminFeature={data.showSuperAdminFeature}
              updateToastMessage={updateToastMessage}
            />
          </ErrorBoundary>
        </StyledMasonry>
      )}
      <ToastMessage
        isOpen={toastMessageOptions.isOpen}
        messageType={toastMessageOptions.type}
        closeToastMessage={(): void => {
          updateToastMessage({
            ...toastMessageOptions,
            isOpen: false,
          });
        }}
      >
        <span>{toastMessageOptions.message}</span>
      </ToastMessage>
    </StyledContainer>
  );
};

export default BillingActivityContainer;
